import React from 'react'
import Spinner from '../../../interaxDesignSystemComponents/Spinner'
import { useLatestFeedbackDataForGamer, useFeedbackReadiness } from '../../../apiLayer/queryHooks/queries'
import { useUser } from '../../../hooks/useUser'
import { PageWrapper } from '../../../shared/components'
import { hasUnreadLatestFeedback, isValidFeedbackData } from './helpers/latestSessionFeedbackHelpers'
import { useFeedbackGeneratingProgress } from './hooks/useFeedbackGeneratingProgress'
import { useLatestSessionFeedbackGlow } from './hooks/useLatestSessionFeedbackGlow'
import { useLatestSessionFeedbackView } from './hooks/useLatestSessionFeedbackView'
import { useMarkLatestFeedbackRead } from './hooks/useMarkFeedbackRead'
import { useLatestFeedbackOnScreen } from './hooks/useLatestFeedbackOnScreen'
import { LatestSessionFeedbackStatePanel } from './components/LatestSessionFeedbackStatePanel'
import { LatestSessionFeedbackContent } from './components/LatestSessionFeedbackContent'

export const LatestSessionFeedbackGamerPageV2: React.FC = () => {
  const { data: feedbackResponse, isLoading, error } = useLatestFeedbackDataForGamer()

  const hasApiError = error || feedbackResponse?.success === false || !!feedbackResponse?.error

  const feedbackReadinessResponse = useFeedbackReadiness({
    enabled: !hasApiError
  })
  const isFeedbackReady = feedbackReadinessResponse?.data?.data?.isReady ?? false
  const hasFeedbackError = feedbackReadinessResponse?.data?.data?.error ?? false

  const { progress } = useFeedbackGeneratingProgress({
    isAnimating: !hasApiError && !isFeedbackReady && !hasFeedbackError
  })

  const { data: userData } = useUser()

  const feedbackData = feedbackResponse?.data
  const isUnread = hasUnreadLatestFeedback(feedbackData, userData)
  const markRead = useMarkLatestFeedbackRead(feedbackData?.sessionId)

  const {
    pendingSections,
    isGlowActive,
    isGlintActive,
    cursorStyles,
    inactiveCursorStyles,
    handleSectionGlintStateChange,
    handleSectionReveal
  } = useLatestSessionFeedbackGlow({
    feedbackData,
    isLoading,
    hasFetchError: !!error,
    isUnread,
    onRead: markRead
  })

  const { profileIndicators, profileTitle, profileDescription, improvementVisualization, sessionTimeFormatted } =
    useLatestSessionFeedbackView(feedbackData)

  const isFeedbackShown = isFeedbackReady && isValidFeedbackData(feedbackData)
  useLatestFeedbackOnScreen(feedbackData, isFeedbackShown)

  if (isLoading) {
    return (
      <PageWrapper
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          ...inactiveCursorStyles
        }}
      >
        <Spinner size="large" color="sun" />
      </PageWrapper>
    )
  }

  if (hasApiError) {
    return (
      <PageWrapper sx={{ ...inactiveCursorStyles }}>
        <LatestSessionFeedbackStatePanel variant="apiError" />
      </PageWrapper>
    )
  }

  if (hasFeedbackError) {
    return (
      <PageWrapper sx={{ ...inactiveCursorStyles }}>
        <LatestSessionFeedbackStatePanel variant="generationFailed" />
      </PageWrapper>
    )
  }

  if (!isFeedbackReady && !hasFeedbackError) {
    return (
      <PageWrapper sx={{ ...inactiveCursorStyles }}>
        <LatestSessionFeedbackStatePanel variant="generating" progress={progress} />
      </PageWrapper>
    )
  }

  if (isFeedbackReady && !isValidFeedbackData(feedbackData)) {
    return (
      <PageWrapper sx={{ ...inactiveCursorStyles }}>
        <LatestSessionFeedbackStatePanel variant="empty" />
      </PageWrapper>
    )
  }

  if (!isFeedbackReady || !isValidFeedbackData(feedbackData)) {
    return null
  }

  return (
    <LatestSessionFeedbackContent
      feedbackData={feedbackData}
      profileIndicators={profileIndicators}
      profileTitle={profileTitle}
      profileDescription={profileDescription}
      improvementVisualization={improvementVisualization}
      sessionTimeFormatted={sessionTimeFormatted}
      isGlowActive={isGlowActive}
      isGlintActive={isGlintActive}
      cursorStyles={cursorStyles}
      pendingSections={pendingSections}
      onSectionReveal={handleSectionReveal}
      onSectionGlintStateChange={handleSectionGlintStateChange}
    />
  )
}

export default LatestSessionFeedbackGamerPageV2
